/**
 * <predictive-search> — header search with live results from /search/suggest?section_id=predictive-search.
 * Markup contract (sections/header.liquid): [data-search-input], [data-search-clear], [data-search-panel],
 * [data-search-default] (popular terms), [data-search-results], [data-search-status].
 * Keyboard: ArrowDown from the input jumps into the results, ArrowUp/Down moves between links, Esc closes (handled by <site-header>).
 */
class PredictiveSearch extends HTMLElement {
  #abort = new AbortController();
  #fetchAbort = null;
  #cache = new Map();

  connectedCallback() {
    const { signal } = this.#abort;
    this.input = this.querySelector('[data-search-input]');
    this.clear = this.querySelector('[data-search-clear]');
    this.panel = this.querySelector('[data-search-panel]');
    this.defaults = this.querySelector('[data-search-default]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.enabled = Boolean(window.RM_CONFIG && window.RM_CONFIG.predictiveSearch);
    if (!this.input) return;

    this.input.addEventListener('input', () => this.#onInput(), { signal });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') this.#focusLink(0, event);
    }, { signal });
    this.clear?.addEventListener('click', () => {
      this.input.value = '';
      this.#onInput();
      this.input.focus();
    }, { signal });
    this.panel?.addEventListener('keydown', (event) => this.#onPanelKey(event), { signal });
    document.addEventListener('search:opened', () => this.#showPanel(), { signal });
    document.addEventListener('search:closed', () => {
      if (this.panel) this.panel.hidden = true;
    }, { signal });
    this.querySelector('form')?.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#fetchAbort?.abort();
  }

  #showPanel() {
    if (!this.enabled || !this.panel) return;
    this.panel.hidden = false;
    this.#onInput();
  }

  #onInput() {
    const term = this.input.value.trim();
    if (this.clear) this.clear.hidden = term.length === 0;
    if (!this.enabled) return;
    if (term.length === 0) {
      if (this.results) this.results.innerHTML = '';
      if (this.defaults) this.defaults.hidden = false;
      return;
    }
    if (this.defaults) this.defaults.hidden = true;
    this.#search(term);
  }

  #search = RM.debounce(async (term) => {
    if (this.input.value.trim() !== term) return;
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    try {
      let html = this.#cache.get(term);
      if (!html) {
        const url = new URL(RM.config.routes.predictiveSearch, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product,query,page,article');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url.toString(), { signal: this.#fetchAbort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        html = await response.text();
        this.#cache.set(term, html);
      }
      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-root]');
      if (!root || !this.results) return;
      this.results.innerHTML = root.innerHTML;
      const count = this.results.querySelector('[data-predictive-count]');
      if (this.status && count) this.status.textContent = count.textContent;
    } catch (error) {
      if (error.name !== 'AbortError' && this.results) this.results.innerHTML = '';
    }
  }, 250);

  #links() {
    return [...(this.panel ? this.panel.querySelectorAll('a[href]') : [])].filter((link) => !link.closest('[hidden]'));
  }

  #focusLink(index, event) {
    const links = this.#links();
    if (links.length === 0) return;
    event?.preventDefault();
    links[Math.max(0, Math.min(index, links.length - 1))].focus();
  }

  #onPanelKey(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = this.#links();
    const index = links.indexOf(document.activeElement);
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && index === 0) return this.input.focus();
    this.#focusLink(index + (event.key === 'ArrowDown' ? 1 : -1));
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
